"use client";

import { LeadForm } from "@2u-landing-pages/components";
import { BLOCKS } from "@contentful/rich-text-types";
import Image from "next/image";
import PropTypes from "prop-types";
import styles from "./Hero.module.scss";

const defaultProps = {
  heroImageDesktop: {
    src: "https://ischoolonline.berkeley.edu/wp-content/uploads/sites/37/2021/10/UCB-CYB_Logo_282x56-1.png",
    alt: "school logo",
    height: 47,
    width: 235,
  },
  heroImageMobile: {
    src: "https://whitelabel.2u.com/cdn/v1/b2b57977-3799-4958-b13b-4db6f983e929/marketing/logo-mobile.png",
    alt: "UC Berkeley Logo",
    height: 62,
    width: 319,
  },
  list: [
    {
      id: "1",
      title: "Complete in 12-20 Months",
      description:
        "Prepare to be a data science leader well-versed in data engineering, machine learning, statistical analysis, and more.",
    },
    {
      id: "2",
      title: "Graduate from a No. 1-Ranked Program",
      description:
        'The online MIDS program from the UC Berkeley School of Information (I School) is nationally recognized by Fortune magazine.<sup><a id="fn1" href="#fn1b">1</a></sup>',
    },
    {
      id: "3",
      title: "Gain In-Demand Skills",
      description:
        'Careers in data science are on the rise<sup><a id="fn2" href="#fn2b">2</a></sup>. Our graduates are applying their skills at Amazon, Apple, Facebook, Microsoft, Google, and other top companies.',
    },
  ],
  title: "UC Berkeley's Master of Information and Data Science | Online",
  subtitle:
    "Leverage the latest tools and analytical methods to work with data at scale, derive insights from complex and unstructured data, and solve real-world problems.",
  showArrow: true,
  arrowColor: "#f2a901",
  listIconColor: "#f2a900",
  baseBackgroundColor: "#003a70",
  baseBackgroundImage:
    "https://ischoolonline.berkeley.edu/wp-content/uploads/sites/37/2021/10/UCB-CYB_Hero_Desktop_1440.jpg",
  titleFont: "Freight Text, serif",
  titleFontSize: "38px",
  formId: "2660",
  formFooter: "No GMAT/GRE required",
};

export const DegreesHero = function (props: any = defaultProps) {
  return (
    <>
      <style>
        {`
          .degrees-hero {
            background-color: ${props.baseBackgroundColor};

            @media (min-width: 768px) {
              background-image: url(${props.baseBackgroundImage});
            }
          }

          .degrees-hero-list {
            li:before {
              color: ${props.listIconColor};
            }
          }

          .degrees-hero-title {
            font-family: ${props.titleFont};
            font-size: ${props.titleFontSize};
          }

          .multi-step-form {
            p.b-body.b-body-p.b-caption-md {
              text-align: center;
              font-size: .875rem;
            }
          }
        `}
      </style>
      {props.customCss && <style>{props.customCss}</style>}
      <div className={`${styles.hero} degrees-hero`}>
        <section>
          <div className={styles.heroContainer}>
            <div className={styles.heroImageDesktop}>
              <Image
                src={props.heroImageDesktop.src}
                alt={props.heroImageDesktop.alt}
                height={props.heroImageDesktop.height}
                width={props.heroImageDesktop.width}
                className={styles.heroImage}
                loading="eager"
              />
            </div>
            <div className={`${styles.heroImageMobile} degrees-hero-image-mobile`}>
              <Image
                src={props.heroImageMobile.src}
                alt={props.heroImageMobile.alt}
                height={props.heroImageMobile.height}
                width={props.heroImageMobile.width}
              />
            </div>
            <div className={styles.heroBody}>
              <h1 className={`${styles.heroBodyHeading} degrees-hero-title`}>
                <span>{props.title}</span>
              </h1>
              <div className={styles.heroBodyText}>
                <span>{props.subtitle}</span>
              </div>
              <div className={styles.heroForm}>
                <div className={`${styles.heroFormContainer} rv-lead-form`} id="form">
                  <LeadForm
                    position="hero"
                    packageId={props.formId}
                    enableStyles={false}
                    loadedClassName={styles.leadFormActive}
                    className="multi-step-form"
                    footnote={
                      props.formFooter && {
                        data: {},
                        content: [
                          {
                            data: {},
                            content: [
                              {
                                data: {},
                                marks: [],
                                value: props.formFooter,
                                nodeType: "text",
                              },
                            ],
                            nodeType: BLOCKS.PARAGRAPH,
                          },
                        ],
                        nodeType: BLOCKS.DOCUMENT,
                      }
                    }
                  />
                </div>
              </div>
              <div className={`${styles.heroList} degrees-hero-list`}>
                <ul>
                  {props.list.map((item: any) => (
                    <li key={item.id}>
                      <strong>{item.title}</strong>
                      &nbsp;—&nbsp;
                      <span dangerouslySetInnerHTML={{ __html: item.description }} />
                    </li>
                  ))}
                </ul>
              </div>
              {props.showArrow && (
                <svg
                  className={styles.arrowSvg}
                  width="142"
                  height="89"
                  viewBox="0 0 142 89"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M139.327 18.5459C141.996 23.3656 141.254 28.8526 140.216 34.1171C140.216 34.1978 140.206 34.2687 140.197 34.3334C140.182 34.4471 140.169 34.5417 140.216 34.6362C140.163 34.7228 140.111 34.8095 140.058 34.8961L140.032 35.1939C139.747 38.3884 139.469 41.5203 139.327 44.7204C139.104 49.3923 138.863 54.0641 138.622 58.736C138.381 63.4069 138.14 68.0778 137.918 72.7487C137.844 75.8629 136.583 76.1595 134.284 74.5283C130.577 72.0072 127.018 69.2637 124.348 65.7046C122.569 63.3318 121.605 63.7025 119.974 65.6304C114.487 72.1555 108.184 77.7167 100.621 81.4983C82.2319 90.7669 61.0995 89.5063 44.2677 83.7969C32.2556 80.8309 23.6543 71.9331 15.4979 63.0352C4.82051 51.3197 -0.0733192 37.3055 0.000829686 21.3635C0.000829686 17.9527 0.594021 17.5078 3.55998 19.3615C8.1572 22.2533 12.0129 26.1091 15.5721 30.2614C18.3897 33.4498 19.502 36.8606 19.3537 41.0871C19.1312 47.3898 20.0952 53.6183 22.1713 59.6243C23.0611 62.1454 24.6182 63.1835 27.1393 64.3699C60.5804 80.0153 85.6428 58.2155 93.9474 50.6523C98.2481 46.7224 102.104 42.3477 105.737 36.5641C105.017 36.7508 104.328 36.928 103.664 37.0986C100.639 37.8761 98.146 38.517 95.6529 39.3076C94.0957 39.8266 92.7611 39.8266 91.4264 38.9368C84.4564 34.4879 78.8952 28.6301 74.3721 21.7343C73.1116 19.7323 73.6306 18.9166 76.0034 18.2493C82.1577 16.4697 88.3122 14.6901 94.3182 12.3915C95.7606 11.84 97.2219 11.3182 98.6851 10.7956C104.894 8.57834 111.137 6.3488 116.118 1.78824C118.342 -0.287935 120.122 -0.584528 122.569 1.04674C129.465 5.64397 135.248 11.3534 139.327 18.5459Z"
                    fill={props.arrowColor}
                  ></path>
                </svg>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

DegreesHero.propTypes = {
  customBaseClass: PropTypes.string,
  heroImageDesktop: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  heroImageMobile: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  showArrow: PropTypes.bool,
  arrowColor: PropTypes.string,
  listIconColor: PropTypes.string,
  baseBackgroundColor: PropTypes.string,
  baseBackgroundImage: PropTypes.string,
  titleFont: PropTypes.string,
  titleFontSize: PropTypes.string,
  formId: PropTypes.string,
  formFooter: PropTypes.string,
};
